import { Skeleton } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useChannelCards, type ChannelCard } from '@entities/channel';
import { extractErrorMessage } from '@shared/api';
import { IconPlus, TopBar } from '@shared/ui/v2';
import { ChBadge, StatusPill, dateRu } from './chUi';
import { hostsOf } from './sites';
import './channels.css';

function subtitle(c: ChannelCard) {
  if (c.platform === 'widget') {
    return c.sites.length
      ? `Окно чата на сайте · ${hostsOf(c.sites).join(', ')}`
      : 'Окно чата на сайте · сайт не указан';
  }
  const where = c.platform === 'telegram' ? 'Бот в Telegram' : 'Бот в MAX';
  return `${where} · подключён ${dateRu(c.created_at)}`;
}

/** Экран 6 «Ассистент → Каналы» (утверждён 28.10.2026). */
export function ChannelsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data, isLoading, error } = useChannelCards(id);
  const list = data ?? [];
  const hasTg = list.some((c) => c.platform === 'telegram');
  const hasMax = list.some((c) => c.platform === 'max');
  const hasWidget = list.some((c) => c.platform === 'widget');

  const more = [
    !hasMax && {
      kind: 'max' as const,
      title: 'MAX',
      text: 'Российский мессенджер. Ответы и рассылки, как в Telegram.',
      to: `/assistants/${id}/channels/new?type=max`,
      btn: 'Подключить',
    },
    {
      kind: 'telegram' as const,
      title: hasTg ? 'Ещё один Telegram-бот' : 'Telegram',
      text: hasTg
        ? 'Например, отдельный бот для другого города или отдела.'
        : 'Бот в мессенджере: ответы клиентам и рассылки.',
      to: `/assistants/${id}/channels/new?type=telegram`,
      btn: 'Подключить',
    },
    !hasWidget && {
      kind: 'widget' as const,
      title: 'Окно чата на сайте',
      text: 'Кнопка чата на вашем сайте — ассистент отвечает посетителям.',
      to: `/assistants/${id}/channels/new?type=widget`,
      btn: 'Подключить',
    },
    hasMax && {
      kind: 'max' as const,
      title: 'Ещё один бот в MAX',
      text: 'Например, отдельный бот для другого города или отдела.',
      to: `/assistants/${id}/channels/new?type=max`,
      btn: 'Подключить',
    },
    {
      kind: 'api' as const,
      title: 'API',
      text: 'Ассистент в вашей программе — настраивается в разделе API.',
      to: '/api-access',
      btn: 'Открыть',
    },
  ].filter(Boolean) as Array<{
    kind: 'max' | 'telegram' | 'widget' | 'api';
    title: string;
    text: string;
    to: string;
    btn: string;
  }>;

  return (
    <>
      <TopBar
        section
        title="Каналы"
        hint="Где ассистент отвечает клиентам. Везде одна модель, инструкция и база знаний."
        actions={
          <Link to={`/assistants/${id}/channels/new`} className="v2-btn is-primary">
            <IconPlus size={15} />
            Подключить канал
          </Link>
        }
      />
      <div
        className="v2-content is-section"
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {error && (
          <div className="v2-card v2-card-pad">
            Не удалось загрузить каналы: {extractErrorMessage(error)}
          </div>
        )}
        {data && (
          <section className="v2-card" aria-labelledby="h-on" style={{ overflow: 'hidden' }}>
            <h2 id="h-on" className="v2-h2 ch-list-head">
              Подключены · {list.length}
            </h2>
            {!list.length && (
              <div className="v2-card-pad v2-muted v2-small">
                Пока ни одного канала. Подключите окно чата на сайт или бота в Telegram или MAX —
                ассистент начнёт отвечать клиентам.
              </div>
            )}
            {list.map((c) => (
              <Link key={c.id} to={`/assistants/${id}/channels/${c.id}`} className="ch-row">
                <ChBadge kind={c.platform} />
                <div className="ch-row-main">
                  <div className="ch-row-title">{c.title}</div>
                  <div className="ch-row-sub v2-xs v2-muted">{subtitle(c)}</div>
                </div>
                <StatusPill status={c.status} />
                <span className="ch-row-count">
                  <b>{c.messages_7d}</b> сообщ. за 7 дн.
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </Link>
            ))}
          </section>
        )}
        {data && (
          <section aria-labelledby="h-more">
            <h2 id="h-more" className="v2-h2" style={{ marginBottom: 10 }}>
              Можно подключить
            </h2>
            <div className="ch-more">
              {more.map((m) => (
                <div key={m.title} className="v2-card ch-more-card">
                  <ChBadge kind={m.kind} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{m.title}</div>
                    <div className="v2-xs v2-muted">{m.text}</div>
                  </div>
                  <Link to={m.to} className="v2-btn">
                    {m.btn}
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
